import React from 'react';
import { FormattedMessage } from 'react-intl';

import LinkButton from '@atlaskit/button/link';
import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading/heading';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';

import { CREATE_CONTENT } from '@confluence/named-routes/entry-points/routes.ts';

import { i18n } from '../blogPostsMacroi18n.ts';

const blogPostsEmptyBg =
	'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAALoAAADwCAMAAACAE47JAAAACVBMVEX///9wcHBwcHDMVYRVAAAAA3RSTlMAESJ8z6YqAAADxklEQVR42u3d0W4bMQxE0bn8/4/uQwMs2gXiLCVqyHbn3drjAQMlsKzod4i/AgBqH+LbAIyUXwGayge+gcgE2pQ+0h9rAS99KD/2BCz0qfrYGgz0ifooCAb6LH2UBQN9Cj5qg4E+AB8HgoHeGx8xFh/nMpgegYHeEh+ng5/ux0eMxYcng+mBke7HR4zFhzWD6YGD7seHP4PpgYHuxcdce8RYfMy1R4zFx1x79EoHejYk6POKp5+dcrrfHh0z2c6/b2ewXXylFV7JdHgnaDGA1z7zDaBtgab2lhuwdofBdgkM9mnlo5qAwT5Ij+rCYLvEYLtgnv0Kg+1isF1isF0MtovBdjHYLgbbRbzHnS12DPa+eDQXr5OZbGewXbz2yz4Vr7l2aSxec+1yZDBdWOl+u1wZTBdz6WIuXeyiOy7J2EHHdE/GMh3fNROs0eNjoKH95y+GZvZn8waN7M9fCU3sV+lOfZ7u1yfp2WC2S7EQ6u2f6X58nu7H5+l+fZ7ux6fpfjxpuh9Pmu7H46JHnLS3u8aJNN2Pz9P9U5On+4tP0/148nS/PU3340nT/XYzPaLW3vXKMgrpfrvU9ex5nu4vPkE32v8Iebrfnqb7Bz5N99upofvtUnS2t6AHSoQKut8u0due/izJb+c7OkPvr9Fcu6TuM8O3dInG9p/vFACt9iaeLgR0safWgb52fQ7Y7SysgvlHda0AnHaW6BJGe4Jepd9Qu/Ec9GrtkgnP8lOVCQ57gl6GX32ksjlv30YXp0eGG92IZ6l27/n5paq0Foy1a5KdG905NAvPch/+x0C32NlNl46NzH66HLVrUzhfu/x2DHSHnRvda8/VLrc9X7vc9nztamEnT6+376udIro4V7ua2DvQVV47ZXSdql1d7LSgU1w7dXRxpPZG3zvuQdeJiWn0tWN60HVgYtTIThM6lbVTSlf5xKiTnS50iifm87EAMNSepLPrQDGlE3OnY/1nbwt0Eo0Yar/TKTgLUjfsN/mOhRfsOXpiZR9dEU+fUj3tJOiJWswTcz2ixk45PR6ktnYe0in8mLZs2G/V2Gt/Ro8otFNJJzGP/onhKr2s9kI6Ea1q9//tfsVAbz4xElFdOy/9lkjFOuzz6Ryg04pOYe289P+dTjzKS99C10vfsyVV0ulE56VvGHbm0vU0NcUY6MZfwHDT87XLTM/Xjpuer73+Mtiq2nHT87XLTM/bZabnV6YZXWTlfrrIyv10kZX76aJQLpl3ay3EunGgleC7VQ4tJlf6Oh6tx3E7MdoTEvJM+Ip2hp3wX1/Ls3M7es6xAAAAAElFTkSuQmCC';

const styles = cssMap({
	container: {
		border: `${token('border.width')} solid ${token('color.border')}`,
		borderRadius: token('radius.small'),
		minHeight: '125px',
		backgroundRepeat: 'no-repeat',
		backgroundPosition: 'right center',
	},
});

type EmptyStateProps = {
	spaceKey?: string;
};

export const EmptyState = ({ spaceKey }: EmptyStateProps): React.JSX.Element => {
	const createBlogPostHref = spaceKey
		? CREATE_CONTENT.toUrl(
				{ spaceKey, contentType: 'blog' },
				{ query: { spaceKey, withFallback: true, source: 'blog-posts-macro' } },
			)
		: undefined;
	return (
		<Box
			padding="space.300"
			testId="blog-posts-empty-state"
			backgroundColor="color.background.neutral.subtle"
			xcss={styles.container}
			// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- backgroundImage requires a dynamic data URI and cannot be expressed in cssMap
			style={{ backgroundImage: `url(${blogPostsEmptyBg})` }}
		>
			<Stack space="space.150">
				<Heading size="medium">
					<FormattedMessage {...i18n.emptyHeading} />
				</Heading>
				<Text color="color.text.subtlest">
					<FormattedMessage {...i18n.emptyContent} />
				</Text>
				{createBlogPostHref && (
					<Box paddingBlockStart="space.100">
						<LinkButton
							appearance="default"
							href={createBlogPostHref}
							testId="create-blog-post-button"
						>
							<FormattedMessage {...i18n.createPost} />
						</LinkButton>
					</Box>
				)}
			</Stack>
		</Box>
	);
};
